import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCopy, Landmark, AlertTriangle } from "lucide-react";
import { eur, itDate } from "@/lib/pa";

const ROLE = { artist: "Artista / Gruppo", supplier: "Fornitore di servizi" };

export function buildMepaText(d, ev) {
  const o = d.operator, a = d.amounts;
  const opt = (v, s) => (v ? s : null);
  const what = d.services.length ? d.services.join(", ") : ROLE[o.role];
  const lines = [
    "DATI PER TRATTATIVA DIRETTA MePA",
    "",
    "OGGETTO DELLA PRESTAZIONE",
    `Prestazione: ${what}${d.duration ? ` (durata ${d.duration})` : ""}`,
    opt(ev.eventType, `Evento: ${ev.eventType}`),
    opt(ev.eventDate, `Data: ${itDate(ev.eventDate)}${ev.eventTime ? ` ore ${ev.eventTime}` : ""}`),
    opt(ev.eventLocation, `Luogo: ${ev.eventLocation}`),
    `Codice CPV: ${d.cpv.map((c) => `${c.code} - ${c.label}`).join("; ")}`,
    "",
    "OPERATORE ECONOMICO",
    `Ragione sociale / Nome: ${o.name}`,
    `Partita IVA: ${o.vatNumber || "non indicata"}`,
    opt(o.taxCode, `Codice fiscale: ${o.taxCode}`),
    opt(o.atecoCode, `Codice ATECO: ${o.atecoCode}`),
    opt(o.address, `Sede: ${o.address}`),
    opt(o.pec, `PEC: ${o.pec}`),
    opt(o.email, `Email: ${o.email}`),
    opt(o.phone, `Telefono: ${o.phone}`),
    "",
    "IMPORTI",
    `Importo netto (base d'asta): ${eur(a.subtotalNet)}`,
    `IVA (${a.vatLabel}): ${eur(a.vat)}`,
    `Totale: ${eur(a.totalGross)}`,
    opt(a.splitPayment, `Split Payment: all'operatore ${eur(a.payableToOperator)}, IVA da versare all'Erario ${eur(a.vatToErario)}`),
    opt(d.client.ipaCode, `Codice Univoco Ufficio (IPA) per la fattura: ${d.client.ipaCode}`),
  ];
  return lines.filter((l) => l !== null).join("\n");
}

export function MepaButton({ provider, distance, optionType, ev }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = async () => {
    try {
      const r = await api.post("/mepa/trattativa", { artistId: provider.id, distanceKm: distance, optionType });
      setData(r.data); setOpen(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const text = data ? buildMepaText(data, ev) : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Dati copiati negli appunti"); }
    catch { toast.error("Copia non riuscita: seleziona il testo e copialo manualmente"); }
  };
  return (
    <>
      <button type="button" data-testid="mepa-generate" onClick={load} className="btn-pill-outline w-full justify-center text-sm"><Landmark className="w-4 h-4"/>Genera Dati per Trattativa Diretta MePA</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="mepa-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-navy">Trattativa Diretta MePA</DialogTitle>
            <DialogDescription>Dati pronti da incollare sulla piattaforma acquistinretepa.it per avviare una Trattativa Diretta con {provider.companyName}.</DialogDescription>
          </DialogHeader>
          {data && !data.operator.hasVat && <p className="text-sm text-amber-700 flex gap-2" data-testid="mepa-no-vat"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>L'operatore non ha indicato una Partita IVA valida: su MePA possono contrarre solo operatori economici abilitati.</p>}
          {data && data.operator.hasVat && !data.operator.mepaEnabled && <p className="text-xs text-slate-500" data-testid="mepa-not-enabled">L'operatore non ha ancora caricato la prova di abilitazione MePA: verifica l'abilitazione sul bando Consip prima di avviare la trattativa.</p>}
          <pre data-testid="mepa-text" className="whitespace-pre-wrap text-xs bg-slate-50 border border-slate-200 rounded-xl p-4 font-mono text-slate-800">{text}</pre>
          <button type="button" data-testid="mepa-copy" onClick={copy} className="btn-pill-gold justify-center"><ClipboardCopy className="w-4 h-4"/>Copia negli Appunti</button>
          <p className="text-[11px] text-slate-500">Importi calcolati con la trasferta e l'opzione indicate nel calcolatore. Verifica i dati prima dell'invio.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
